import React from 'react'
import {
  useNavigate,
  useLocation,
  useSearchParams,
  Routes,
  Route,
  Navigate
} from 'react-router-dom'
import styled from 'styled-components'
import { Layout, Result, Button, Spin } from 'antd'
import {
  ShoppingCartOutlined,
  CalendarOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { useMembership } from '../hooks/useMembership'
import CheckoutPage from './CheckoutPage'
import CheckoutRecordsPage from './CheckoutRecordsPage'
import CheckoutDraftsPage from './CheckoutDraftsPage'
import EmployeeOrderPage from './EmployeeOrderPage'
import EmployeeOrderRecordsPage from './EmployeeOrderRecordsPage'
import RevenueRecordsPage from './RevenueRecordsPage'
import InstallmentsPage from './InstallmentsPage'

const { Content } = Layout

const ManagementContainer = styled(Layout)`
  /* 透明背景，避免跟 App.js 外層白卡產生「灰色夾層」 */
  background: transparent;
  min-height: auto;
  display: flex;
  flex-direction: column;
`

const ContentWrapper = styled(Content)`
  /* 純粹的內容容器：透明、無框、最小 padding（App.js 已有 padding） */
  margin: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;
  min-height: 280px;
`

const CheckoutTabsWrap = styled.div`
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-bottom: 16px;
    margin-left: -8px;
    margin-right: -8px;
  }
`

const TabsList = styled.div`
  display: flex;
  gap: 0;
  background: #f5f5f7;
  border-radius: 12px;
  padding: 4px;
  border: 1px solid #e5e5e7;

  @media (max-width: 768px) {
    border-radius: 10px;
    margin-top: ${(props) => (props.$isDraftsPage ? 0 : 100)}px;
    margin-left: 24px;
    margin-right: 24px;
    margin-bottom: 0;
    box-sizing: border-box;
    min-height: 48px;
  }
`

const TabItem = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  color: #6e6e73;
  background: transparent;
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s,
    box-shadow 0.2s;

  &:hover {
    color: #1d1d1f;
    background: rgba(255, 255, 255, 0.7);
  }

  &.active {
    color: #1d1d1f;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .tab-icon {
    font-size: 18px;
  }

  @media (max-width: 768px) {
    padding: 12px 10px;
    font-size: 14px;
    gap: 6px;

    .tab-icon {
      font-size: 16px;
    }
  }
`

const CheckoutManagementPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { hasFeature, permissionsLoaded } = useMembership()

  const pathname = (location.pathname || '').replace(/\/$/, '') || '/'
  const segments = pathname.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1]
  const isInCheckoutArea = segments.includes('checkout')
  const isCheckoutPage =
    isInCheckoutArea && (lastSegment === 'checkout' || segments.length === 1)
  const isDraftsPage = isInCheckoutArea && lastSegment === 'drafts'
  const showCheckoutTabs = isInCheckoutArea && (isCheckoutPage || isDraftsPage)
  const hasBookingId = !!searchParams.get('bookingId')
  const activeTab = isDraftsPage
    ? 'drafts'
    : hasBookingId
      ? 'booking'
      : 'walkin'

  const handleTabWalkin = () => {
    navigate('/checkout/checkout')
  }
  const handleTabBooking = () => {
    navigate('/booking-detail')
  }
  const handleTabDrafts = () => {
    navigate('/checkout/drafts')
  }

  // 權限檢查
  if (!permissionsLoaded) {
    return (
      <ManagementContainer>
        <ContentWrapper>
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <Spin size='large' tip='正在載入權限...' />
          </div>
        </ContentWrapper>
      </ManagementContainer>
    )
  }

  if (!hasFeature('checkout')) {
    return (
      <ManagementContainer>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要更高級別的會員方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </ManagementContainer>
    )
  }

  return (
    <ManagementContainer>
      <ContentWrapper>
        {showCheckoutTabs && (
          <CheckoutTabsWrap>
            <TabsList $isDraftsPage={isDraftsPage}>
              <TabItem
                type='button'
                className={activeTab === 'walkin' ? 'active' : ''}
                onClick={handleTabWalkin}
              >
                <ShoppingCartOutlined className='tab-icon' />
                現場結帳
              </TabItem>
              <TabItem
                type='button'
                className={activeTab === 'booking' ? 'active' : ''}
                onClick={handleTabBooking}
              >
                <CalendarOutlined className='tab-icon' />
                預約結帳
              </TabItem>
              <TabItem
                type='button'
                className={activeTab === 'drafts' ? 'active' : ''}
                onClick={handleTabDrafts}
              >
                <FileTextOutlined className='tab-icon' />
                草稿列表
              </TabItem>
            </TabsList>
          </CheckoutTabsWrap>
        )}
        <Routes>
          <Route path='/' element={<Navigate to='checkout' replace />} />
          <Route path='checkout' element={<CheckoutPage />} />
          <Route path='drafts' element={<CheckoutDraftsPage />} />
          <Route path='records' element={<CheckoutRecordsPage />} />
          <Route path='employee-order' element={<EmployeeOrderPage />} />
          <Route
            path='employee-records'
            element={<EmployeeOrderRecordsPage />}
          />
          <Route path='revenue-records' element={<RevenueRecordsPage />} />
          <Route path='installments' element={<InstallmentsPage />} />
        </Routes>
      </ContentWrapper>
    </ManagementContainer>
  )
}

export default CheckoutManagementPage
